import { useMemo } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { observer } from 'mobx-react'

import { OrderDetailsCard } from 'Modules/orders/components/OrderDetailsCard/OrderDetailsCard'
import { OrderPresentationCard } from 'Modules/orders/components/OrderPresentationCard/OrderPresentationCard'

import { EmployeeEntityCard } from 'Components/EmployeeEntityCard/EmployeeEntityCard'
import { EntityListCard } from 'Components/EntityListCard/EntityListCard'
import type { EntityProps } from 'Components/EntityListCard/EntityListCard.types'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageHeader } from 'Components/PageHeader/PageHeader'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'
import { OrderShipmentTrackingCard } from 'Components/ShipmentTrackingCard/Order/OrderShipmentTrackingCard'

import { useIsOrganisation } from 'Hooks'

import type {
  getOrderDetails,
  getOrderDetailsVariables,
} from 'Constants/graphql/queries/detail/__generated__/getOrderDetails'
import { GET_ORDER_DETAILS } from 'Constants/graphql/queries/detail/GetOrderDetails'
import { PortalSectionEnum } from 'Constants/portalSection'

import { CaseItemStatus, CaseItemType } from 'Portal/__generated__/globalTypes'
import { GLOBAL_ROUTE_PATHS } from 'Portal/src/router/Router.constants'
import type { DeviceTypeEnum } from 'Portal/src/types/deviceTypes'
import { dashOrSpaceToUnderscore } from 'Portal/src/utilities'
import { composePath } from 'Portal/src/utilities/composePath/composePath.util'
import { getCaseItemTypeTranslationId } from 'Portal/src/utilities/getCaseItemTypeTranslationId/getCaseItemTypeTranslationId.util'

export const Order = observer(() => {
  const intl = useIntl()
  const isOrganisation = useIsOrganisation()
  const { employeeId, organisationId, orderId } = useParams<{
    employeeId?: string
    organisationId?: string
    orderId?: string
  }>()

  const {
    loading: isLoading,
    data,
    error,
  } = useQuery<getOrderDetails, getOrderDetailsVariables>(GET_ORDER_DETAILS, {
    skip: !orderId,
    variables: { employeeId, id: (orderId ?? '').toString(), organisationId },
  })

  const rentalEntry: EntityProps[] = useMemo(() => {
    const rental =
      data?.caseItem.status === CaseItemStatus.done
        ? data?.caseItem.subCaseItems?.find(subCaseItem =>
            subCaseItem
              ? dashOrSpaceToUnderscore(subCaseItem.caseType) ===
                CaseItemType.new_device
              : false,
          )?.rental || data?.caseItem.rental
        : data?.caseItem.rental

    if (!rental) {
      return []
    }

    const { stocklistItem, id, deviceDefinition } = rental

    const imei = stocklistItem?.imei ? stocklistItem.imei : stocklistItem?.imei2

    const content = imei ? (
      <p key={id} className="m-0 fs-6">
        IMEI: <span>{imei}</span>
      </p>
    ) : null

    const imagePlaceholderType = deviceDefinition?.productType
      ? (deviceDefinition.productType as DeviceTypeEnum)
      : undefined

    const link = composePath({
      isOrganisation,
      params: {
        employeeId,
        organisationId,
        rentalId: id,
      },
      paths: {
        employee: GLOBAL_ROUTE_PATHS.employee.rental,
        organisation: GLOBAL_ROUTE_PATHS.organisation.rental,
      },
    })

    return [
      {
        content,
        id,
        imagePlaceholderType,
        imageSource: deviceDefinition?.imageURL,
        link,
        title:
          deviceDefinition?.name ||
          intl.formatMessage({ id: 'RentalEntityCard_rental_id' }, { id }),
      },
    ]
  }, [
    data?.caseItem.rental,
    data?.caseItem.status,
    data?.caseItem.subCaseItems,
    employeeId,
    intl,
    isOrganisation,
    organisationId,
  ])

  if (isLoading) {
    return <PageLoadingState />
  }

  if (error) {
    return <PageErrorState />
  }

  if (!data) {
    return <PageErrorState />
  }

  const { caseItem } = data
  const title = getCaseItemTypeTranslationId(caseItem.caseType)
  const QRCode = caseItem?.generatedQRCode || ''

  return (
    <Container className="pt-6" fluid>
      <div role="banner">
        <PageHeader portalSection={PortalSectionEnum.OrderDetails} />
      </div>

      <Row>
        <Col xxl={8}>
          <OrderPresentationCard
            customOrderDeviceName={caseItem.offerOrderItem?.customDeviceName}
            deviceDefinition={caseItem.data.deviceDefinition}
            isPurchase={caseItem.offerOrderItem?.addonType === 'purchase'}
            requestedOfferOrder={caseItem.data.requestedOfferOrder}
            requestedRentCategory={caseItem.data.requestedRentCategory}
            status={caseItem.status}
          />

          <OrderDetailsCard caseItem={caseItem} title={title} />
        </Col>

        <Col xxl={4}>
          <Row>
            <OrderShipmentTrackingCard QRCode={QRCode} caseItem={caseItem} />

            {isOrganisation && (
              <Col lg={6} xs={12} xxl={12}>
                <EmployeeEntityCard
                  employee={caseItem.assignedEmployee ?? null}
                />
              </Col>
            )}

            {rentalEntry.length > 0 && (
              <Col lg={6} xs={12} xxl={12}>
                <EntityListCard
                  entities={rentalEntry}
                  title="Order_linked_rental"
                />
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </Container>
  )
})
